import { orderFetch, qcDone, qcFetch, qcItemUpdate } from "@/apis/inventoryv2";
import SText from "@/styles/SText";
import { AddIQCItem, QCItem, IQCOrder, IQCResEnum, OrderV2, QCStateEnum } from "@/types/inventoryV2";

import { Button, Flex, Modal, ModalProps, notification, Spin, Typography } from "antd";

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useIqcEnums from "../hooks/useIqcEnums";
import AddIQCForm, { AddIQCFormRef, SubmitData, SubmitItem } from "./AddIQC/AddIQCForm";
import AddWrap from "./AddIQC/style";

interface IQCDetailProps extends Omit<ModalProps, "onClose"> {
  detail: IQCOrder;
  onClose: (type: "CANCEL" | QCStateEnum) => void;
}
const IQCDetail: FC<IQCDetailProps> = ({ detail, onClose, ...modalProps }) => {
  const { t } = useTranslation();
  const { getQCState } = useIqcEnums();
  const formRef = useRef<AddIQCFormRef>(null);

  const [qcOrder, setQcOrder] = useState<IQCOrder>(detail);
  const [sourceOrder, setSourceOrder] = useState<OrderV2 | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (detail) getData();
  }, [detail]);

  const isComplete = useMemo(() => [QCStateEnum.QC_PASS, QCStateEnum.QC_FAIL].includes(qcOrder.qcState), [qcOrder])

  const getData = async (onlyQc?: boolean): Promise<IQCOrder | null> => {
    setLoading(true);
    try {
      let qc = await qcFetch(detail.id);
      if (!!!onlyQc) {
        let source = await orderFetch(detail.sourceOrderId);
        setSourceOrder({ ...source });
      };
      setQcOrder({ ...qc });
      setLoading(false);
      return qc;
    } catch (error) {
      setLoading(false);
      notification.error({ message: t('warehouse.fetchErr') });
      return null;
    }
  };

  const hasUpdata = (data: SubmitData) => {

    let dataMapList: Array<{ orderItem: QCItem, dataItem: SubmitItem }> = [];
    data.items.forEach(item => {
      dataMapList.push({ dataItem: { ...item }, orderItem: (qcOrder?.items || []).find(oi => item.id === oi.id)! })
    });
    let updateItems: Array<SubmitItem> = [];
    let keyList: Array<keyof AddIQCItem> = [
      "checkCount",
      "passCount",
      "failCount",
      "qcResult",
      "defectType",
      "disposition",
      "inspectorId",
      "remarks"
    ];

    for (let i = 0; i < dataMapList.length; i++) {
      let data = dataMapList[i];
      for (let j = 0; j < keyList.length; j++) {
        let key = keyList[j];
        if (data.dataItem[key] !== data.orderItem[key]) {
          updateItems.push({ ...data.dataItem })
          break
        }
      }
    }

    return updateItems;
  };

  const handleOk = useCallback(async (isDone?: boolean) => {
    if (loading) return;
    const data = formRef.current?.getSubmitData();
    if (!data) return;

    const newItems = hasUpdata(data);
    const hasUpdate = newItems.length > 0;

    // 无更新且非完成操作，直接提示并返回
    if (!hasUpdate && !isDone) {
      notification.warning({ message: t('warehouse.noUpdate') });
      return;
    }

    try {
      setLoading(true);

      let latestQcOrder: IQCOrder | null = null;

      // 如果有更新，先更新数据
      if (hasUpdate) {
        await Promise.all(
          newItems.map(item => qcItemUpdate(item.id, { ...item }))
        );
        // 更新后刷新数据，并获取最新的 qcOrder
        latestQcOrder = await getData(true);
        // 仅保存时提示成功
        if (!isDone) {
          notification.success({ message: t('warehouse.updateSucc') });
        }
      }

      // 完成操作
      if (isDone) {
        // 使用最新数据检查是否有未质检项
        const currentQcOrder = latestQcOrder || qcOrder;
        const notResItems = currentQcOrder.items?.filter(
          item => item.qcResult !== IQCResEnum.FAIL && item.qcResult !== IQCResEnum.PASS
        ) || [];

        if (notResItems.length > 0) {
          notification.error({
            message: t('warehouse.canNotPass'),
            description: (
              <>
                <div>{t('warehouse.canNotPassTip')}</div>
                <span>{notResItems.map(item => item.product.code).join(', ')}</span>
              </>
            )
          });
        } else {
          let res = await qcDone(qcOrder.id);
          notification.success({ message: t('warehouse.qcDone', { number: qcOrder.number }) });
          onClose(res.qcState);
        }
      }
    } catch (error: any) {
      const tip = error === 73505 ? t('warehouse.73505') : '';
      notification.error({
        message: t('warehouse.notPass'),
        description: tip
      });
    } finally {
      setLoading(false);
    }
  }, [loading, t, qcOrder.id, qcOrder, hasUpdata, getData, formRef]);


  const stateInfo = useMemo(() => {
    return getQCState(qcOrder.qcState)
  }, [qcOrder]);

  return (
    <Modal
      title={
        <Flex justify="space-between" style={{ paddingRight: 30 }} >
          <Flex gap={8} align="center" >
            <Typography.Title level={4} style={{ margin: 0 }}>{t('warehouse.iqcCheck')}: {detail.number}</Typography.Title>
            {stateInfo && <SText tagMode text={stateInfo.label} color={stateInfo.color} tagStyle={{ width: 140 }} />}
          </Flex>
          {qcOrder && [QCStateEnum.QC_ING, QCStateEnum.QC_TODO].includes(qcOrder.qcState) && <Button type="primary" onClick={() => handleOk(true)} >{t('warehouse.done')}</Button>}
        </Flex>
      }
      onCancel={() => onClose("CANCEL")}
      open
      width={1200}
      onOk={() => handleOk(false)}
      okText={t('common.save')}
      footer={isComplete ? null : undefined}
      {...modalProps}
    >
      <Spin spinning={loading}>
        <AddWrap>
          {sourceOrder && <AddIQCForm formType="EDIT" formRef={formRef} order={sourceOrder} qcOrder={qcOrder} readonly={isComplete} />}
        </AddWrap>
      </Spin>
    </Modal>
  )
};

export default IQCDetail;